/*
  Cart drawer accessibility — remove button.

  The cart drawer is rendered by UpCart, a third-party app, inside the shadow
  root of #upCart. Its per-item remove control is an icon-only <button> holding
  a bare trash <svg>: no text, no aria-label, no title. Assistive technology
  therefore announces it as an unnamed button, and a shopper cannot tell what it
  does or which of several items it would remove.

  The drawer's other icon controls (quantity plus/minus, the quantity field) do
  carry aria-labels from the app, so only the remove button needs naming here.

  The app's markup is not editable from the theme, so the name is applied from
  the outside:
    - the button's accessible name is composed from the item's own title and
      variant, so with several items in the cart each remove button is
      distinguishable by name alone (WCAG 2.4.6, 4.1.2);
    - the trash icon is hidden from assistive technology, leaving the button
      with exactly one name rather than a name plus an unlabelled graphic.

  UpCart is a React app that re-renders the item list on every add, remove, and
  quantity change, discarding the attributes we set. A MutationObserver on the
  shadow root reapplies them after each render. Writes are made only when the
  value actually differs, so the observer settles instead of looping.
*/
(() => {
  const SELECTORS = {
    host: '#upCart',
    removeButton: '[class*="component-product-tile__remove-button"]',
    itemInfo: '[class*="component-product-tile__info"]',
    itemTitle: '[class*="component-product-tile__product-title"]',
    itemVariant: '[class*="component-product-tile__variant"]',
  };
  const HOST_POLL_MS = 250;
  const HOST_POLL_LIMIT = 60;

  /**
   * Fills the placeholder in the translated remove string with an item's
   * description. The description is substituted rather than appended so it
   * keeps the position the translator chose.
   * @param {string} description Product title, with variant where there is one.
   * @returns {string} The finished label, or '' when the string is missing.
   */
  function removeLabelFor(description) {
    const translation = (window.PXUTheme && window.PXUTheme.translation) || {};

    return String(translation.cart_remove_item || '').replace(/%%product%%/g, description);
  }

  /**
   * Reads the text of a node, collapsing the whitespace UpCart's markup leaves
   * between the title link and its wrapper.
   * @param {Element|null} node Element to read.
   * @returns {string} Collapsed text, or '' when the node is absent.
   */
  function textOf(node) {
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /**
   * Builds the description of the item a remove button belongs to — its title,
   * plus the variant when the item has one, so two sizes of the same product
   * do not produce two identically named buttons.
   * @param {Element} button A remove button inside the drawer.
   * @returns {string} The item's description, or '' when the title is missing.
   */
  function describeItem(button) {
    const info = button.closest(SELECTORS.itemInfo);

    if (!info) {
      return '';
    }

    const title = textOf(info.querySelector(SELECTORS.itemTitle));
    const variant = textOf(info.querySelector(SELECTORS.itemVariant));

    if (!title) {
      return '';
    }

    return variant ? `${title}, ${variant}` : title;
  }

  /**
   * Names every remove button in the drawer and hides its icon from assistive
   * technology. A button whose item has no readable title is left alone rather
   * than given a name with a gap in it.
   * @param {ShadowRoot} root The drawer's shadow root.
   * @returns {void}
   */
  function labelRemoveButtons(root) {
    root.querySelectorAll(SELECTORS.removeButton).forEach(button => {
      const label = removeLabelFor(describeItem(button));

      // Only write when the value is actually different, so this does not
      // retrigger the observer that called us.
      if (label && button.getAttribute('aria-label') !== label) {
        button.setAttribute('aria-label', label);
      }

      button.querySelectorAll('svg').forEach(icon => {
        if (icon.getAttribute('aria-hidden') !== 'true') {
          icon.setAttribute('aria-hidden', 'true');
          icon.setAttribute('focusable', 'false');
        }
      });
    });
  }

  /**
   * Labels the drawer now and after every re-render.
   * @param {ShadowRoot} root The drawer's shadow root.
   * @returns {void}
   */
  function watchDrawer(root) {
    labelRemoveButtons(root);

    new MutationObserver(() => labelRemoveButtons(root)).observe(root, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Waits for the app to mount its shadow root. UpCart loads as a script tag
   * outside the theme, so there is no load event to hook; polling stops once
   * the root appears, and gives up rather than running for the page's life if
   * the app is absent or blocked.
   * @returns {void}
   */
  function whenDrawerReady() {
    let attempts = 0;

    const timer = window.setInterval(() => {
      const host = document.querySelector(SELECTORS.host);

      if (host && host.shadowRoot) {
        window.clearInterval(timer);
        watchDrawer(host.shadowRoot);
        return;
      }

      attempts += 1;

      if (attempts >= HOST_POLL_LIMIT) {
        window.clearInterval(timer);
      }
    }, HOST_POLL_MS);
  }

  whenDrawerReady();
})();
